import axios, { AxiosError, AxiosInstance, AxiosRequestHeaders } from 'axios';
import { createApiClient } from '@/utils/api/ApiDefault';
import { PortalToken } from '@/types/api';
import { deleteCookie, getCookie, setCookie } from '@/utils/tokens';
import {
  COOKIE_KEY_ACCESS_TOKEN,
  COOKIE_KEY_DISPLAY_TYPE,
  COOKIE_KEY_REFRESH_TOKEN,
} from '@/constant/keys';
import {
  rnPermissionDeny,
  requestRNToken,
  sendSentry,
  RnMessage,
  RetryableRequestConfig,
  setAuthorizationHeader,
} from '@/utils/function';
import { PAGE_URL } from '@/constant/url';

const axiosInstance = createApiClient();
export const API_TIMEOUT = 8000;
axiosInstance.defaults.timeout = API_TIMEOUT;

// refresh 전용(인터셉터 미적용) 클라이언트
const refreshClient: AxiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  timeout: API_TIMEOUT,
});

// 전역 단일 refresh Promise
let refreshPromise: Promise<string> | null = null;

// 공통 로그아웃(세션 정리 + 이동)
// - 타입을 never로 지정해 이후 코드 경로가 없음을 TS에 명확히 알림
function forceLogout(errCode: number): never {
  try {
    deleteCookie(COOKIE_KEY_ACCESS_TOKEN);
    deleteCookie(COOKIE_KEY_REFRESH_TOKEN);
    deleteCookie(COOKIE_KEY_DISPLAY_TYPE);
  } catch {
    console.log('catchError : deleteCookie');
  }
  if ((window as any).isRNWebview) {
    rnPermissionDeny(errCode);
  } else {
    window.location.href = `${PAGE_URL.LOGIN}?redirect=${encodeURIComponent(
      window.location.pathname + window.location.search,
    )}`;
  }
  // 여기까지 도달하면 런타임 제어는 이미 이동된 상태라고 가정하고 강제로 throw
  throw new Error('LOGOUT');
}

// 실제 토큰 리프레시
async function performRefresh(rt: string): Promise<string> {
  try {
    const res = await refreshClient.post<PortalToken>(
      '/api/auth/portal/token',
      {
        grant_type: 'refresh_token',
        refresh_token: rt,
      },
    );

    const token = res.data.data;
    const { access_token, refresh_token, refresh_expires_in, expires_in } =
      token;

    setCookie(COOKIE_KEY_ACCESS_TOKEN, access_token, expires_in);
    setCookie(COOKIE_KEY_REFRESH_TOKEN, refresh_token, refresh_expires_in);
    setCookie(COOKIE_KEY_DISPLAY_TYPE, 'image', expires_in);

    return access_token;
  } catch (err) {
    sendSentry(err, 'JWT_REFRESH_FAILED');
    forceLogout(11); // never 반환 → 이후 코드 도달 불가
  }
}

async function getOrCreateRefreshPromise(): Promise<string> {
  if (refreshPromise) {
    // non-null 보장 경로: 지역 변수에 담아 반환하면 TS가 null 아님을 인식
    const p = refreshPromise;
    return p;
  }

  const rt = getCookie(COOKIE_KEY_REFRESH_TOKEN);
  if (!rt || rt === 'undefined') {
    forceLogout(12); // never
  }

  // 생성되면 반드시 Promise<string>만 저장되도록 보장
  const p = performRefresh(rt as string).finally(() => {
    refreshPromise = null;
  });

  refreshPromise = p; // 타입: Promise<string>
  return p;
}

// 요청 인터셉터: Authorization 주입
axiosInstance.interceptors.request.use(async (config) => {
  const extraAuth = (config.headers as any)?.Authorization;
  if (!extraAuth) {
    const accessToken = getCookie(COOKIE_KEY_ACCESS_TOKEN);
    if (accessToken && accessToken !== 'undefined') {
      // Axios v1에서는 AxiosHeaders일 수 있으므로 set 지원 확인 후 분기
      const h = config.headers as any;
      if (h && typeof h.set === 'function') {
        h.set('Authorization', `Bearer ${accessToken}`);
      } else {
        // fallback: 기존 헤더 유지 + 키만 덮어쓰기
        const base =
          (config.headers as AxiosRequestHeaders) ??
          ({} as AxiosRequestHeaders);
        (base as any).Authorization = `Bearer ${accessToken}`;
        config.headers = base;
      }
    }
  }
  return config;
});

// 응답 인터셉터: 401/2002 처리
axiosInstance.interceptors.response.use(
  (response) => response,
  async (err: AxiosError) => {
    const originalRequest = (err.config || {}) as RetryableRequestConfig;

    // 타임아웃 / 네트워크 에러
    if ((err as any).code === 'ECONNABORTED') {
      console.log('Request timeout: 8 seconds');
      sendSentry(err, 'Timeout');
      return Promise.reject(err);
    }

    const status = err?.response?.status;
    const code = (err?.response?.data as any)?.code;

    // refresh 엔드포인트 자체는 재시도/재귀 금지
    const isRefreshCall = (originalRequest.url || '').includes(
      '/api/auth/portal/token',
    );

    if (!isRefreshCall && (status === 401 || code === '2002')) {
      if (originalRequest._retry) {
        forceLogout(13); // never
      }
      if ((window as any).isRNWebview2) {
        await requestRNToken(RnMessage.GET_TOKEN_INFO);
        const newAccessToken = getCookie(COOKIE_KEY_ACCESS_TOKEN);
        setAuthorizationHeader(originalRequest, newAccessToken as string);
        return axiosInstance(originalRequest);
      }

      const rt = getCookie(COOKIE_KEY_REFRESH_TOKEN);
      if (!rt || rt === 'undefined') {
        forceLogout(14); // never
      }

      try {
        const newAccessToken = await getOrCreateRefreshPromise();

        setAuthorizationHeader(originalRequest, newAccessToken);

        return axiosInstance(originalRequest);
      } catch (e) {
        // performRefresh 내부에서 forceLogout()으로 종료됨(never)
        forceLogout(15);
      }
    }

    // HTTP 4xx/5xx 또는 상태코드가 없는 네트워크 오류만 로깅
    if (!status || (status >= 400 && status < 600)) {
      sendSentry(err, 'JWT');
    }

    return Promise.reject(err);
  },
);

export { axiosInstance as jwtInstance };
